import { useState } from "react";
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@runcheetah/api/router";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@runcheetah/ui";

import {
  CodeText,
  PageHeader,
  QueryErrorState,
  SegmentedControl,
  TableEmptyRow,
  TableShell,
  formatMoneyUsd,
} from "@/components/common";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatNullableDateTimeWithSeconds } from "@/lib/date-format";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";

type RouterInputs = inferRouterInputs<AppRouter>;
type RouterOutputs = inferRouterOutputs<AppRouter>;
type ModelPricingDiagnosticsInput = RouterInputs["admin"]["modelPricing"]["diagnostics"];
type ModelPricingBackfillInput = RouterInputs["admin"]["modelPricing"]["repriceUnpricedUsage"];
type ModelPricingDiagnostics = RouterOutputs["admin"]["modelPricing"]["diagnostics"];
type ModelPricingBackfillResult = RouterOutputs["admin"]["modelPricing"]["repriceUnpricedUsage"];
type ModelPricingRow = ModelPricingDiagnostics["rows"][number];
type ModelPricingUsageSummary = ModelPricingDiagnostics["usageSummaries"][number];
type ModelPricingStatus = ModelPricingRow["status"];
type ModelPricingRequirement = ModelPricingRow["requiredBy"][number];
type ModelPricingBackfillEligibility =
  ModelPricingDiagnostics["recentUnpricedEvents"][number]["backfillEligibility"];
type ModelPricingRange = "7d" | "30d" | "90d";
type ModelPricingUnpricedUsageBreakdown = Pick<
  ModelPricingUsageSummary,
  | "classifiedUnpricedEventCount"
  | "backfillEligibleUnpricedEventCount"
  | "skippedMissingPriceCount"
  | "skippedMissingUsageCount"
  | "skippedUnpriceableUsageCount"
>;

const BACKFILL_LIMIT = 500;

const RANGE_OPTIONS = [
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
] as const;

const STATUS_CONFIG: Record<
  ModelPricingStatus,
  { label: string; description: string; className: string }
> = {
  missing_price: {
    label: "Missing price",
    description: "No llm_model_price row exists for this provider/model.",
    className: "border-red-200 bg-red-50 text-red-700",
  },
  empty_price: {
    label: "Empty price",
    description: "A price row exists, but all rate columns are null.",
    className: "border-orange-200 bg-orange-50 text-orange-700",
  },
  incomplete_price: {
    label: "Incomplete price",
    description: "A text model price row is missing either flat input or output pricing.",
    className: "border-yellow-200 bg-yellow-50 text-yellow-700",
  },
  has_unpriced_usage: {
    label: "Unpriced usage",
    description: "The model has a price row now, but selected-window usage events were unpriced.",
    className: "border-amber-200 bg-amber-50 text-amber-700",
  },
  ok: {
    label: "Ready",
    description: "A price row exists and selected-window usage is priced.",
    className: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
};

const BACKFILL_ELIGIBILITY_CONFIG: Record<
  ModelPricingBackfillEligibility,
  { label: string; description: string; className: string }
> = {
  eligible: {
    label: "Eligible",
    description: "Has token usage and matching price coverage.",
    className: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
  missing_price: {
    label: "Missing price",
    description: "No matching provider/model price row exists.",
    className: "border-red-200 bg-red-50 text-red-700",
  },
  missing_usage: {
    label: "Missing usage",
    description: "No stored token usage can be priced.",
    className: "border-slate-200 bg-slate-50 text-slate-700",
  },
  unpriceable_usage: {
    label: "Unpriceable",
    description: "Stored usage needs rate columns this price row lacks.",
    className: "border-amber-200 bg-amber-50 text-amber-700",
  },
};

const getLookbackDaysForRange = (range: ModelPricingRange): number =>
  range === "7d" ? 7 : range === "90d" ? 90 : 30;

export const buildModelPricingDiagnosticsInput = (
  range: ModelPricingRange,
): ModelPricingDiagnosticsInput => ({
  lookbackDays: getLookbackDaysForRange(range),
  unpricedLimit: 30,
});

export const buildModelPricingBackfillInput = (
  range: ModelPricingRange,
): ModelPricingBackfillInput => ({
  lookbackDays: getLookbackDaysForRange(range),
  limit: BACKFILL_LIMIT,
});

export const countModelPricingBackfillSkippedRows = (
  result: Pick<
    ModelPricingBackfillResult,
    | "skippedMissingPriceCount"
    | "skippedMissingUsageCount"
    | "skippedUnpriceableUsageCount"
    | "skippedConcurrentUpdateCount"
  >,
): number =>
  result.skippedMissingPriceCount +
  result.skippedMissingUsageCount +
  result.skippedUnpriceableUsageCount +
  result.skippedConcurrentUpdateCount;

export const countModelPricingUnpricedUsageBreakdown = (
  summaries: readonly ModelPricingUnpricedUsageBreakdown[],
): ModelPricingUnpricedUsageBreakdown =>
  summaries.reduce(
    (counts, summary) => ({
      classifiedUnpricedEventCount:
        counts.classifiedUnpricedEventCount + summary.classifiedUnpricedEventCount,
      backfillEligibleUnpricedEventCount:
        counts.backfillEligibleUnpricedEventCount + summary.backfillEligibleUnpricedEventCount,
      skippedMissingPriceCount: counts.skippedMissingPriceCount + summary.skippedMissingPriceCount,
      skippedMissingUsageCount: counts.skippedMissingUsageCount + summary.skippedMissingUsageCount,
      skippedUnpriceableUsageCount:
        counts.skippedUnpriceableUsageCount + summary.skippedUnpriceableUsageCount,
    }),
    {
      classifiedUnpricedEventCount: 0,
      backfillEligibleUnpricedEventCount: 0,
      skippedMissingPriceCount: 0,
      skippedMissingUsageCount: 0,
      skippedUnpriceableUsageCount: 0,
    },
  );

export const formatModelPricingUnpricedUsageBreakdown = (
  breakdown: ModelPricingUnpricedUsageBreakdown,
  options?: { unpricedEventCount?: number },
): string => {
  const detail = `${formatInteger(breakdown.backfillEligibleUnpricedEventCount)} eligible / ${formatInteger(
    breakdown.skippedMissingPriceCount,
  )} missing price / ${formatInteger(breakdown.skippedMissingUsageCount)} missing usage / ${formatInteger(
    breakdown.skippedUnpriceableUsageCount,
  )} unpriceable`;
  const unpricedEventCount = options?.unpricedEventCount;

  if (
    unpricedEventCount !== undefined &&
    breakdown.classifiedUnpricedEventCount < unpricedEventCount
  ) {
    return `${detail} (classified ${formatInteger(
      breakdown.classifiedUnpricedEventCount,
    )} of ${formatInteger(unpricedEventCount)})`;
  }

  return detail;
};

export const formatModelPricingBackfillSkippedBreakdown = (
  result: Pick<
    ModelPricingBackfillResult,
    | "skippedMissingPriceCount"
    | "skippedMissingUsageCount"
    | "skippedUnpriceableUsageCount"
    | "skippedConcurrentUpdateCount"
  >,
): string =>
  `${formatInteger(result.skippedMissingPriceCount)} missing price, ${formatInteger(
    result.skippedMissingUsageCount,
  )} missing usage, ${formatInteger(result.skippedUnpriceableUsageCount)} unpriceable, ${formatInteger(
    result.skippedConcurrentUpdateCount,
  )} changed concurrently`;

export const getModelPricingBackfillEligibilityLabel = (
  eligibility: ModelPricingBackfillEligibility,
): string => BACKFILL_ELIGIBILITY_CONFIG[eligibility].label;

export const getModelPricingStatusLabel = (status: ModelPricingStatus): string =>
  STATUS_CONFIG[status].label;

export const countModelPricingRowsByStatus = (rows: ModelPricingRow[]) =>
  rows.reduce(
    (counts, row) => ({
      ...counts,
      [row.status]: counts[row.status] + 1,
    }),
    {
      missing_price: 0,
      empty_price: 0,
      incomplete_price: 0,
      has_unpriced_usage: 0,
      ok: 0,
    } satisfies Record<ModelPricingStatus, number>,
  );

export const formatModelPriceRate = (value: number | null): string =>
  value === null ? "-" : `$${value.toFixed(value >= 10 ? 2 : 4)}/1M`;

const formatDateTime = (value: string | null): string =>
  value ? formatNullableDateTimeWithSeconds(value) : "-";

const formatInteger = (value: number): string => new Intl.NumberFormat("en-US").format(value);

export const formatModelPricingBackfillResultSummary = (
  result: ModelPricingBackfillResult,
): string =>
  `Backfilled ${formatInteger(result.repricedCount)} usage events and restored ${formatMoneyUsd(
    result.totalEstimatedCostUsd,
    { precision: 4 },
  )}. Scanned ${formatInteger(result.scannedCount)} unpriced rows; skipped ${formatInteger(
    countModelPricingBackfillSkippedRows(result),
  )}: ${formatModelPricingBackfillSkippedBreakdown(result)}.`;

const getRequirementLabel = (requirement: ModelPricingRequirement): string => {
  if (requirement.kind === "recent_usage") return "Usage in window";
  if (requirement.component) return requirement.component;
  if (requirement.runtimeFlagName) return requirement.runtimeFlagName;
  return requirement.label;
};

function StatusBadge({ status }: { status: ModelPricingStatus }) {
  const config = STATUS_CONFIG[status];
  return <Badge className={config.className}>{config.label}</Badge>;
}

function MetricCard({
  label,
  value,
  detail,
  tone = "neutral",
}: {
  label: string;
  value: string;
  detail: string;
  tone?: "neutral" | "danger" | "warning" | "good";
}) {
  return (
    <Card
      className={cn(
        "overflow-hidden border bg-card shadow-xs",
        tone === "danger" && "border-red-200 bg-red-50/50",
        tone === "warning" && "border-amber-200 bg-amber-50/50",
        tone === "good" && "border-emerald-200 bg-emerald-50/40",
      )}
    >
      <CardContent className="p-4">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div className="mt-3 text-3xl font-semibold tracking-tight text-foreground">{value}</div>
        <div className="mt-2 text-xs leading-5 text-muted-foreground">{detail}</div>
      </CardContent>
    </Card>
  );
}

function RuntimeModelGrid({ diagnostics }: { diagnostics: ModelPricingDiagnostics }) {
  const items = [
    ["API chat", diagnostics.runtimeModels.apiOpenAiChatModel],
    ["Worker default", diagnostics.runtimeModels.openAiModel],
    ["Analysis", diagnostics.runtimeModels.analysisModel],
    ["Generation", diagnostics.runtimeModels.generationModel],
    ["Image generation", diagnostics.runtimeModels.openAiImageGenerationModel],
    ["Image QA", diagnostics.runtimeModels.socialPostImageQaModel],
    ["Planner", diagnostics.runtimeModels.socialPostPlannerModel],
    ["Copywriter", diagnostics.runtimeModels.socialPostCopywriterModel],
  ] as const;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Effective Runtime Models</CardTitle>
        <p className="text-sm text-muted-foreground">
          Resolved from admin runtime flags, including the social image QA override.
        </p>
      </CardHeader>
      <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {items.map(([label, value]) => (
          <div key={label} className="rounded-2xl border bg-muted/20 p-3">
            <div className="text-xs text-muted-foreground">{label}</div>
            <CodeText className="mt-1 block truncate">{value}</CodeText>
          </div>
        ))}
        <div className="rounded-2xl border bg-muted/20 p-3">
          <div className="text-xs text-muted-foreground">Social image provider</div>
          <CodeText className="mt-1 block truncate">
            {diagnostics.runtimeModels.socialPostImageRealizerProvider}
          </CodeText>
        </div>
      </CardContent>
    </Card>
  );
}

function ModelPricingTable({ rows }: { rows: ModelPricingRow[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Provider / Model Coverage</CardTitle>
        <p className="text-sm text-muted-foreground">
          Runtime-required models, pricing overrides, and models seen in the selected usage window.
        </p>
      </CardHeader>
      <CardContent>
        <TableShell>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>Provider / Model</TableHead>
                <TableHead>Required By</TableHead>
                <TableHead>Rates</TableHead>
                <TableHead className="text-right">Usage in Window</TableHead>
                <TableHead>Last Used</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableEmptyRow colSpan={6}>
                    No runtime or selected-window usage models found.
                  </TableEmptyRow>
                </TableRow>
              ) : null}
              {rows.map((row) => (
                <TableRow key={`${row.provider}:${row.model}`}>
                  <TableCell className="align-top">
                    <div className="space-y-1">
                      <StatusBadge status={row.status} />
                      <div className="max-w-44 text-xs text-muted-foreground">
                        {STATUS_CONFIG[row.status].description}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="align-top">
                    <div className="font-medium">{row.provider}</div>
                    <CodeText className="mt-1 block max-w-72 truncate">{row.model}</CodeText>
                    {row.price ? (
                      <Badge variant="outline" className="mt-2 bg-background text-[10px]">
                        {row.price.source}
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell className="align-top">
                    <div className="flex max-w-80 flex-wrap gap-1.5">
                      {row.requiredBy.map((requirement) => (
                        <Badge key={requirement.key} variant="outline" className="bg-background">
                          {getRequirementLabel(requirement)}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="align-top text-xs">
                    {row.price ? (
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                        <span className="text-muted-foreground">Input</span>
                        <span>{formatModelPriceRate(row.price.inputPerMillion)}</span>
                        <span className="text-muted-foreground">Output</span>
                        <span>{formatModelPriceRate(row.price.outputPerMillion)}</span>
                        <span className="text-muted-foreground">Cache read</span>
                        <span>{formatModelPriceRate(row.price.cacheReadPerMillion)}</span>
                        <span className="text-muted-foreground">Cache write</span>
                        <span>{formatModelPriceRate(row.price.cacheWritePerMillion)}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">No price row</span>
                    )}
                  </TableCell>
                  <TableCell className="align-top text-right text-sm">
                    <div>{formatInteger(row.usage?.eventCount ?? 0)} events</div>
                    <div
                      className={cn(
                        "text-xs",
                        (row.usage?.unpricedEventCount ?? 0) > 0
                          ? "text-amber-700"
                          : "text-muted-foreground",
                      )}
                    >
                      {formatInteger(row.usage?.unpricedEventCount ?? 0)} unpriced
                    </div>
                    {row.usage && row.usage.unpricedEventCount > 0 ? (
                      <div className="mt-1 text-[11px] leading-4 text-muted-foreground">
                        {formatModelPricingUnpricedUsageBreakdown(row.usage, {
                          unpricedEventCount: row.usage.unpricedEventCount,
                        })}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell className="align-top text-sm text-muted-foreground">
                    {formatDateTime(row.usage?.lastUsedAt ?? null)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableShell>
      </CardContent>
    </Card>
  );
}

function RecentUnpricedEventsTable({
  events,
}: {
  events: ModelPricingDiagnostics["recentUnpricedEvents"];
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Recent Unpriced Usage Events</CardTitle>
        <p className="text-sm text-muted-foreground">
          Latest events where estimated cost was not recorded. These are candidates for backfilling
          once the current price table has coverage.
        </p>
      </CardHeader>
      <CardContent>
        <TableShell>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Created</TableHead>
                <TableHead>Provider / Model</TableHead>
                <TableHead>Usage Category</TableHead>
                <TableHead>Backfill</TableHead>
                <TableHead>Component</TableHead>
                <TableHead>Worker Job</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.length === 0 ? (
                <TableRow>
                  <TableEmptyRow colSpan={6}>
                    No recent unpriced events in this window.
                  </TableEmptyRow>
                </TableRow>
              ) : null}
              {events.map((event) => (
                <TableRow key={event.id}>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDateTime(event.createdAt)}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{event.provider}</div>
                    <CodeText className="mt-1 block max-w-72 truncate">{event.model}</CodeText>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-background">
                      {event.usageCategory}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <Badge
                        className={BACKFILL_ELIGIBILITY_CONFIG[event.backfillEligibility].className}
                      >
                        {BACKFILL_ELIGIBILITY_CONFIG[event.backfillEligibility].label}
                      </Badge>
                      <div className="max-w-48 text-xs text-muted-foreground">
                        {BACKFILL_ELIGIBILITY_CONFIG[event.backfillEligibility].description}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {event.component ?? "-"}
                  </TableCell>
                  <TableCell>
                    {event.workerJobId ? (
                      <CodeText className="block max-w-56 truncate">{event.workerJobId}</CodeText>
                    ) : (
                      <span className="text-sm text-muted-foreground">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableShell>
      </CardContent>
    </Card>
  );
}

export function ModelPricingPage() {
  const [range, setRange] = useState<ModelPricingRange>("30d");
  const input = buildModelPricingDiagnosticsInput(range);
  const utils = trpc.useUtils();
  const diagnosticsQuery = trpc.admin.modelPricing.diagnostics.useQuery(input);
  const syncMutation = trpc.admin.modelPricing.sync.useMutation({
    onSuccess: async () => {
      await utils.admin.modelPricing.diagnostics.invalidate();
    },
  });
  const backfillMutation = trpc.admin.modelPricing.repriceUnpricedUsage.useMutation({
    onSuccess: async () => {
      await utils.admin.modelPricing.diagnostics.invalidate();
    },
  });

  if (diagnosticsQuery.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (diagnosticsQuery.error) {
    return (
      <QueryErrorState
        title="Unable to load model pricing diagnostics"
        message={diagnosticsQuery.error.message}
        isForbidden={diagnosticsQuery.error.data?.code === "FORBIDDEN"}
      />
    );
  }

  const diagnostics = diagnosticsQuery.data;
  const statusCounts = countModelPricingRowsByStatus(diagnostics.rows);
  const unpricedEventCount = diagnostics.usageSummaries.reduce(
    (sum, item) => sum + item.unpricedEventCount,
    0,
  );
  const unpricedUsageBreakdown = countModelPricingUnpricedUsageBreakdown(
    diagnostics.usageSummaries,
  );
  const totalCostUsd = diagnostics.usageSummaries.reduce((sum, item) => sum + item.totalCostUsd, 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Model Pricing"
        description="Audit runtime model coverage, current price rows, and selected-window unpriced LLM usage."
        meta={`Checked ${formatDateTime(diagnostics.checkedAt)}`}
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <SegmentedControl options={RANGE_OPTIONS} value={range} onChange={setRange} />
            <Button
              variant="outline"
              disabled={syncMutation.isPending}
              onClick={() => syncMutation.mutate()}
            >
              {syncMutation.isPending ? "Syncing..." : "Sync prices now"}
            </Button>
            <Button
              variant="outline"
              disabled={backfillMutation.isPending || unpricedEventCount === 0}
              onClick={() => backfillMutation.mutate(buildModelPricingBackfillInput(range))}
            >
              {backfillMutation.isPending ? "Backfilling..." : "Backfill costs"}
            </Button>
          </div>
        }
      />

      {syncMutation.error ? (
        <Card className="border-red-200 bg-red-50/60">
          <CardContent className="py-3 text-sm text-red-700">
            {syncMutation.error.message}
          </CardContent>
        </Card>
      ) : null}

      {backfillMutation.error ? (
        <Card className="border-red-200 bg-red-50/60">
          <CardContent className="py-3 text-sm text-red-700">
            {backfillMutation.error.message}
          </CardContent>
        </Card>
      ) : null}

      {syncMutation.data ? (
        <Card className="border-emerald-200 bg-emerald-50/60">
          <CardContent className="py-3 text-sm text-emerald-800">
            Synced {syncMutation.data.upsertedCount} price rows from {syncMutation.data.apiUrl}.
          </CardContent>
        </Card>
      ) : null}

      {backfillMutation.data ? (
        <Card className="border-emerald-200 bg-emerald-50/60">
          <CardContent className="py-3 text-sm text-emerald-800">
            {formatModelPricingBackfillResultSummary(backfillMutation.data)}
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        <MetricCard
          label="Missing Prices"
          value={formatInteger(statusCounts.missing_price)}
          detail={`${formatInteger(statusCounts.empty_price)} empty price rows`}
          tone={statusCounts.missing_price > 0 ? "danger" : "good"}
        />
        <MetricCard
          label="Unpriced Usage"
          value={formatInteger(unpricedEventCount)}
          detail={`${diagnostics.lookbackDays} day lookback; ${formatModelPricingUnpricedUsageBreakdown(
            unpricedUsageBreakdown,
            { unpricedEventCount },
          )}`}
          tone={unpricedEventCount > 0 ? "warning" : "good"}
        />
        <MetricCard
          label="Price Rows"
          value={formatInteger(diagnostics.priceRows.length)}
          detail={`${formatInteger(diagnostics.pricingSync.overrideCount)} runtime override prices`}
        />
        <MetricCard
          label="Window Cost"
          value={formatMoneyUsd(totalCostUsd, { precision: 2 })}
          detail={`${formatInteger(diagnostics.usageSummaries.length)} active provider/model pairs`}
        />
        <MetricCard
          label="Pricing Sync"
          value={diagnostics.pricingSync.enabled ? "Enabled" : "Disabled"}
          detail={`${diagnostics.pricingSync.cron} / ${diagnostics.pricingSync.providerAllowList.join(", ")}`}
          tone={diagnostics.pricingSync.enabled ? "good" : "warning"}
        />
      </div>

      <RuntimeModelGrid diagnostics={diagnostics} />
      <ModelPricingTable rows={diagnostics.rows} />
      <RecentUnpricedEventsTable events={diagnostics.recentUnpricedEvents} />
    </div>
  );
}
